import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/constants';

/**
 * Custom hook for managing projects
 * Handles loading projects, URL parameter parsing, and project filtering
 */
export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [urlProjectIds, setUrlProjectIds] = useState([]);
  const [preSelectedProject, setPreSelectedProject] = useState(null);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState(null);

  // Parse URL parameters for projectId
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectIdParams = params.getAll('projectId'); // Get all projectId parameters
    
    // Handle both multiple parameters (?projectId=1&projectId=2) 
    // and comma-separated values (?projectId=1,2)
    const projectIds = [];
    projectIdParams.forEach(param => {
      if (param.includes(',')) {
        // Split comma-separated values
        projectIds.push(...param.split(',').map(id => id.trim()).filter(id => id));
      } else {
        projectIds.push(param.trim());
      }
    });
    
    // Remove duplicates
    setUrlProjectIds([...new Set(projectIds.filter(id => id))]);
  }, []);

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Filter projects based on URL parameters and auto-select if single project
  useEffect(() => {
    if (projects.length === 0) return;

    if (urlProjectIds.length === 0) {
      // No URL parameters - show all projects
      setFilteredProjects(projects);
      setPreSelectedProject(null);
    } else {
      // Filter projects by URL project IDs
      const filtered = projects.filter(project => urlProjectIds.includes(project.id));
      
      // Check if all URL project IDs are valid
      const validProjectIds = filtered.map(p => p.id);
      const invalidIds = urlProjectIds.filter(id => !validProjectIds.includes(id));
      
      if (invalidIds.length > 0) {
        // Some invalid IDs - show all projects (fallback to default behavior)
        setFilteredProjects(projects);
        setPreSelectedProject(null);
      } else if (filtered.length === 1) {
        // Single valid project from URL - auto-select and hide dropdown
        setFilteredProjects(filtered);
        setPreSelectedProject(filtered[0]);
        setSelectedProject(filtered[0].id);
      } else if (filtered.length > 1) {
        // Multiple valid projects from URL - show only these in dropdown
        setFilteredProjects(filtered);
        setPreSelectedProject(null);
      } else {
        // No valid projects found - show all projects (fallback)
        setFilteredProjects(projects);
        setPreSelectedProject(null);
      }
    }
  }, [projects, urlProjectIds]);

  const loadProjects = async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/projects`);
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Failed to load projects: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use the text or default message
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      setProjects(data.projects || []);
      setProjectsError(null);
    } catch (error) {
      console.error('Error loading projects:', error);
      let errorMessage = 'Failed to load projects.';
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Cannot connect to server. Please make sure the server is running at ' + API_BASE_URL;
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'Failed to load projects. Please make sure the server is running.';
      }
      
      setProjectsError(errorMessage);
      // Set empty array on error to prevent UI issues
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : projectId;
  };

  return {
    projects,
    filteredProjects,
    selectedProject,
    setSelectedProject,
    preSelectedProject,
    projectsLoading,
    projectsError,
    loadProjects,
    getProjectName,
  };
}

